<script lang="ts">
    import type {Document, Comment} from 'typings/portaro.be.types';
    import KpGenericPanel from 'shared/ui-widgets/panel/KpGenericPanel.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import KpLoadingInline from 'shared/components/kp-loading/KpLoadingInline.svelte';
    import {getDateFormatter, getInjector, getLocalization} from 'core/svelte-context/context';
    import {KpCommentsPresenter} from './kp-comments.presenter';
    import {popInAnim, popOutAnim} from 'shared/animations/pop-animations';
    import {onMount} from 'svelte';
    import {pipe} from 'core/utils';
    import {loc} from 'shared/utils/pipes';
    import {flip} from 'svelte/animate';
    import {exists} from 'shared/utils/custom-utils';

    export let document: Document;
    export let allowAnonymousComments: boolean;

    const localize = getLocalization();
    const dateFormatter = getDateFormatter();
    const presenter = getInjector().getByClass(KpCommentsPresenter);
    const commentsFlipParams = {duration: 250};

    let loading = true;
    let comments: Comment[] = [];

    const handleCommentAddClick = async (anonymous: boolean) => {
        const addedComment = anonymous ? await presenter.addAnonymousComment(document) : await presenter.addNonAnonymousComment(document);

        if (exists(addedComment)) {
            comments = [...comments, addedComment];
        }
    };

    const handleCommentDeleteClick = async (comment: Comment) => {
        const successful = await presenter.deleteComment(comment);

        if (successful) {
            comments = comments.filter((c) => comment.id !== c.id);
        }
    };

    onMount(async () => {
        comments = await presenter.loadComments(document);
        loading = false;
    });
</script>

<div class="document-comments">
    <KpGenericPanel hasBodyPadding={false}>
        <svelte:fragment slot="heading">
            <h2 class="unset-style">{localize(/* @kp-localization detail.komentare */ 'detail.komentare')}</h2>

            {#if loading}
                <KpLoadingInline size="xs"/>
            {:else}
                <span class="badge comment-count-badge">{comments.length}</span>
            {/if}
        </svelte:fragment>

        <svelte:fragment>
            {#if loading}
                <div class="loading-container">
                    <KpLoadingBlock size="xs"/>
                </div>
            {/if}

            <ul class="comments-list">
                {#each comments as comment(comment.id)}
                    <li class="comment"
                        animate:flip={commentsFlipParams}
                        in:popInAnim={{key: comment.id}}
                        out:popOutAnim={{key: comment.id}}>

                        <div class="header-body-group">
                            <p class="content">{comment.content}</p>

                            {#if comment.deletable}
                                <div class="action-buttons">
                                    <KpButton buttonSize="xs"
                                              additionalClasses="comment-delete-btn"
                                              on:click={() => handleCommentDeleteClick(comment)}>
                                        <IconedContent icon="trash">
                                            {localize(/* @kp-localization commons.Smazat */ 'commons.Smazat')}
                                        </IconedContent>
                                    </KpButton>
                                </div>
                            {/if}
                        </div>

                        <div class="comment-footer">
                            <span class="comment-author text-muted">
                                {#if comment.creator}
                                    {pipe(comment.creator, loc())}
                                {:else}
                                    {localize(/* @kp-localization commons.anonymniCtenar */ 'commons.anonymniCtenar')}
                                {/if}
                            </span>

                            <span class="comment-date text-muted">{pipe(comment.date, dateFormatter('d.M.yyyy HH:mm'))}</span>
                        </div>
                    </li>
                {/each}
            </ul>

            {#if comments.length === 0 && !loading}
                <div class="no-comments text-muted text-center">
                    {localize(/* @kp-localization commons.ZadnePolozky */ 'commons.ZadnePolozky')}
                </div>
            {/if}

            <div class="comments-footer">
                <div class="buttons-container">
                    <KpButton on:click={() => handleCommentAddClick(false)}>
                        <IconedContent icon="comment-user">
                            {localize(/* @kp-localization comments.AddNonAnonymousComment */ 'comments.AddNonAnonymousComment')}
                        </IconedContent>
                    </KpButton>

                    {#if allowAnonymousComments}
                        <KpButton
                            on:click={() => handleCommentAddClick(true)}>
                            <IconedContent icon="comment-alt">
                                {localize(/* @kp-localization comments.AddAnonymousComment */ 'comments.AddAnonymousComment')}
                            </IconedContent>
                        </KpButton>
                    {/if}
                </div>

                {#if !presenter.authEvided}
                    <div class="text-muted">
                        {#if allowAnonymousComments}
                            {localize(/* @kp-localization detail.nejstePrihlasenProNeanonymniKomentarSePrihaste */ 'detail.nejstePrihlasenProNeanonymniKomentarSePrihaste')}
                        {:else}
                            {localize(/* @kp-localization detail.proPridaniKomentareSePrihlaste */ 'detail.proPridaniKomentareSePrihlaste')}
                        {/if}
                    </div>
                {/if}
            </div>
        </svelte:fragment>
    </KpGenericPanel>
</div>

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";

    .document-comments {
        .comment-count-badge {
            margin-left: 4px;
        }

        .loading-container,
        .no-comments {
            padding: 25px @panel-body-padding;
            border-bottom: 1px solid @panel-default-border;
        }

        .comments-list {
            display: flex;
            flex-direction: column;

            .comment {
                padding: @padding-base-horizontal @panel-body-padding;
                border-bottom: 1px solid @panel-default-border;
                transition: background-color 0.2s ease-in-out;

                &:hover {
                    background-color: @table-bg-hover;
                }

                .header-body-group {
                    display: flex;
                    align-items: start;
                    padding-bottom: 10px;

                    .action-buttons {
                        display: flex;
                        flex-direction: column;
                        gap: @padding-base-vertical;
                    }

                    .content {
                        flex: 1;
                    }
                }

                .comment-footer {
                    display: flex;
                    gap: @padding-small-horizontal;
                    align-items: baseline;
                    justify-content: end;

                    .comment-date {
                        font-size: @font-size-small;
                    }
                }
            }
        }

        .comments-footer {
            display: flex;
            padding: @panel-body-padding;
            flex-direction: column;
            align-items: center;
            gap: @padding-large-vertical;
            justify-content: center;

            .buttons-container {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: @padding-small-horizontal;
            }
        }
    }
</style>